import { useEffect, useMemo, useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import { Play, X } from "lucide-react";
import { fetchVideos } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = ["All", "Application", "Demo", "Science", "Clinical"];

export default function VideoCenter() {
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetchVideos().then(setVideos).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? videos : videos.filter((v) => v.category === filter)),
    [filter, videos]
  );

  return (
    <section data-testid="video-center" className="bg-cloud py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">Video Center</div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl text-slate1 leading-tight">
              See XcellerEYES in practice.
            </h2>
            <p className="mt-6 text-lg text-slate1/65">
              Hands-on demonstrations, application techniques, and the science behind lyophilization — from leading clinicians.
            </p>
          </div>
        </ScrollReveal>

        <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="video-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`video-filter-${c.toLowerCase()}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                filter === c
                  ? "bg-navy text-white"
                  : "bg-white text-slate1/70 border border-slate1/10 hover:border-teal hover:text-teal"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 md:gap-8">
          {filtered.map((v, i) => (
            <ScrollReveal key={v.id} delay={i * 80}>
              <button
                onClick={() => { setOpen(v); trackEvent("video_play", { video_id: v.youtube_id, title: v.title }); }}
                data-testid={`video-card-${v.id}`}
                className="group block w-full text-left rounded-3xl overflow-hidden bg-slate1 relative hover:-translate-y-1 transition-all duration-500 shadow-[0_20px_50px_-30px_rgba(0,45,114,0.3)] hover:shadow-[0_30px_70px_-30px_rgba(0,45,114,0.4)]"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={`https://i.ytimg.com/vi/${v.youtube_id}/maxresdefault.jpg`}
                    onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`; }}
                    alt={v.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate1 via-slate1/30 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-20 w-20 rounded-full bg-teal/90 backdrop-blur flex items-center justify-center group-hover:scale-110 group-hover:bg-teal2 transition-all duration-300 shadow-[0_18px_40px_-12px_rgba(21,184,184,0.7)]">
                      <Play className="h-8 w-8 text-white ml-1" fill="white" />
                    </div>
                  </div>
                </div>
                <div className="p-6 text-white">
                  <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.2em] uppercase font-bold">
                    <span className="text-teal2">{v.category}</span>
                    <span className="text-white/30">·</span>
                    <span className="text-white/55">{v.duration}</span>
                    <span className="text-white/30">·</span>
                    <span className="text-white/55">{v.year}</span>
                  </div>
                  <h3 className="font-display text-xl font-medium leading-tight">{v.title}</h3>
                </div>
              </button>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {open && (
        <div
          data-testid="video-modal"
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
        >
          <button
            data-testid="video-close"
            onClick={() => setOpen(null)}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
          >
            <X className="h-6 w-6" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://www.youtube.com/embed/${open.youtube_id}?autoplay=1&rel=0`}
              title={open.title}
              className="w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
